import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Rss, Target, Brain, Bell, Shield, ArrowUpRight, ArrowRight, Check,
  ShieldCheck, Globe, Newspaper, Network, Megaphone, Building2, Briefcase,
  Users, User, TrendingUp, Clock, Mail, MessageSquare, Webhook, Smartphone,
  Lock, Star, MapPin, Minus, SlidersHorizontal, Link2, LayoutDashboard, Radio,
  FileText, LineChart, Settings, ListFilter,
} from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SignalSequence from "@/components/SignalSequence";
import ProductStory from "@/components/ProductStory";

const SITE_URL = "https://newzer.de";
const OG_IMAGE = `${SITE_URL}/brand/newzer-og.png`;
const eur = (n) => new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n));

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

/* Sanfte Einblendung (rein CSS, wie Hero – erscheint zuverlässig, kann nie leer bleiben). */
function Reveal({ children, className = "", style, as: Tag = "div", ...rest }) {
  return <Tag className={`fc-fade-up ${className}`} style={style} {...rest}>{children}</Tag>;
}

/* Abstrakte Informations-/Signal-Landschaft im Hero (dezent, nur ab md, dekorativ). */
function SignalNetwork() {
  const nodes = [
    { x: 70, y: 120, r: 3, t: "info" }, { x: 150, y: 60, r: 2.5, t: "info" },
    { x: 250, y: 150, r: 3, t: "info" }, { x: 210, y: 250, r: 2.5, t: "info" },
    { x: 340, y: 80, r: 2.5, t: "info" }, { x: 120, y: 300, r: 2.5, t: "info" },
    { x: 300, y: 300, r: 3, t: "dev" }, { x: 400, y: 200, r: 3.5, t: "dev" },
    { x: 470, y: 120, r: 2.5, t: "info" }, { x: 440, y: 320, r: 2.5, t: "info" },
    { x: 360, y: 380, r: 2.5, t: "info" }, { x: 520, y: 240, r: 3, t: "dev" },
    { x: 250, y: 210, r: 8, t: "signal" },
  ];
  const links = [
    [0, 1], [1, 4], [0, 5], [5, 3], [3, 12], [12, 2], [2, 4], [4, 8], [12, 6],
    [6, 7], [7, 11], [7, 4], [11, 9], [6, 10], [12, 3], [8, 11],
  ];
  const col = { info: "#1E40AF", dev: "#F47765", signal: "#DC2626" };
  return (
    <svg aria-hidden="true" className="hidden lg:block pointer-events-none absolute right-0 top-0 h-full w-[58%] opacity-70"
      viewBox="0 0 600 460" preserveAspectRatio="xMidYMid slice">
      <g stroke="#13243A" strokeOpacity="0.10" strokeWidth="1">
        {links.map(([a, b], i) => (
          <line key={i} x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y} />
        ))}
      </g>
      {nodes.map((n, i) => (
        <g key={i}>
          {n.t === "signal" && (
            <circle cx={n.x} cy={n.y} r={n.r} fill={col.signal} fillOpacity="0.5" className="fc-signal-ring" />
          )}
          <circle cx={n.x} cy={n.y} r={n.r} fill={col[n.t]}
            className={n.t !== "signal" ? "fc-node-pulse" : ""}
            style={{ animationDelay: `${(i % 5) * 0.5}s` }} />
          {n.t === "signal" && <circle cx={n.x} cy={n.y} r={n.r + 3} fill="none" stroke={col.signal} strokeOpacity="0.35" strokeWidth="1.5" />}
        </g>
      ))}
    </svg>
  );
}

/* Hero-Signal-Card: reine UI-Komponente, als DEMO gekennzeichnet – Herzstück von NEWZER. */
function SignalCard() {
  const meta = [
    ["Relevanz", "Hoch", "text-fc-coral"],
    ["Entwicklung", "Neu", "text-status-attention"],
    ["Quellen", "4", "text-fc-ink"],
    ["Region", "Kommunal", "text-fc-ink"],
  ];
  const channels = [[Mail, "E-Mail"], [MessageSquare, "SMS"], [Webhook, "Webhook"], [Smartphone, "Push · bald"]];
  return (
    <div className="relative bg-white border border-fc-border rounded-2xl shadow-[0_50px_100px_-40px_rgba(19,36,58,0.45)] p-6 max-w-md w-full" data-testid="hero-signal-card">
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-status-critical bg-status-critical/10 rounded-md px-2 py-1 whitespace-nowrap">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-status-critical opacity-60 status-pulse text-status-critical" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-status-critical" />
          </span>
          Wichtiges Signal
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-fc-ink/55 whitespace-nowrap inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> vor 3 Min.</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-fc-ink/40 bg-fc-subtle rounded px-1.5 py-0.5">Demo</span>
        </div>
      </div>
      <h3 className="mt-3 font-head font-bold text-xl text-fc-ink leading-tight">
        Kritik an geplantem Doppelhaushalt nimmt zu
      </h3>
      <p className="mt-2 text-sm text-fc-ink/60 leading-relaxed">
        Mehrere unabhängige Medien greifen dasselbe Thema auf – die Aufmerksamkeit nimmt deutlich zu.
      </p>
      <SignalSequence className="mt-4 rounded-lg border border-fc-border bg-fc-subtle/40 px-3 py-2.5" />
      <div className="mt-4 grid grid-cols-2 gap-2.5">
        {meta.map(([k, v, c]) => (
          <div key={k} className="rounded-lg border border-fc-border bg-fc-subtle/50 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wider text-fc-ink/55">{k}</div>
            <div className={`mt-0.5 text-sm font-semibold ${c}`}>{v}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 text-xs text-fc-ink/60">
        <span className="inline-flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-fc-coral" /> Dynamik steigt</span>
        <span className="text-fc-ink/30">·</span>
        <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-fc-mint" /> Quellen verifiziert</span>
      </div>
      <div className="mt-4 p-3 rounded-lg bg-fc-subtle border border-fc-border text-sm text-fc-ink/75">
        <div className="font-semibold text-fc-ink inline-flex items-center gap-1.5"><Bell className="w-3.5 h-3.5 text-fc-blue" /> NEWZER hat Sie informiert.</div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
          {channels.map(([Icon, label], i) => (
            <span key={label} className={`inline-flex items-center gap-1 ${i === 3 ? "text-fc-ink/40" : "text-fc-mint"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-fc-border">
      {/* Dezentes Foto-Motiv im Hintergrund (nur lg+, sehr zurückhaltend – Leitstellen-Look bleibt ruhig). */}
      <div aria-hidden="true" className="hidden lg:block absolute inset-y-0 right-0 w-[62%] pointer-events-none">
        <picture>
          <source type="image/avif" srcSet="/hero/frueh-900.avif 900w, /hero/frueh-1264.avif 1264w" sizes="900px" />
          <source type="image/webp" srcSet="/hero/frueh-900.webp 900w, /hero/frueh-1264.webp 1264w" sizes="900px" />
          <img src="/hero/frueh-1264.jpg" alt="" loading="eager" decoding="async" className="h-full w-full object-cover opacity-[0.12]" />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-fc-ivory via-fc-ivory/70 to-fc-ivory/30" />
      </div>
      {/* Mobil: dezentes Hochkant-Motiv, sehr zurückhaltend hinter dem Inhalt. */}
      <div aria-hidden="true" className="lg:hidden absolute inset-0 pointer-events-none">
        <picture>
          <source type="image/avif" srcSet="/hero/frueh-m-640.avif 640w, /hero/frueh-m-860.avif 860w" sizes="100vw" />
          <source type="image/webp" srcSet="/hero/frueh-m-640.webp 640w, /hero/frueh-m-860.webp 860w" sizes="100vw" />
          <img src="/hero/frueh-m-640.jpg" alt="" loading="eager" decoding="async" className="h-full w-full object-cover object-top opacity-[0.08]" />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-b from-fc-ivory/85 via-fc-ivory/70 to-fc-ivory/90" />
      </div>
      <SignalNetwork />
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-10 pt-14 sm:pt-20 lg:pt-28 pb-14 lg:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="fc-fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.16em] uppercase text-fc-ink/70 bg-white border border-fc-border rounded-full px-3 py-1.5" data-testid="hero-live-status">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-fc-mint opacity-60 status-pulse text-fc-mint" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-fc-mint" />
              </span>
              Live · NEWZER beobachtet öffentliche Quellen
            </span>
            <h1 className="mt-6 hero-headline text-fc-ink" data-testid="hero-title">
              Wissen,<br /><span className="text-fc-blue">bevor es wichtig wird.</span>
            </h1>
            <p className="mt-5 text-xl sm:text-2xl font-head font-bold text-fc-ink" data-testid="hero-category">
              Das Frühwarnsystem für öffentliche Informationen.
            </p>
            <p className="mt-2 text-base font-semibold text-fc-blue" data-testid="hero-brandline">
              Sie suchen nicht. NEWZER beobachtet.
            </p>
            <p className="mt-4 text-base lg:text-lg text-fc-ink/70 max-w-xl leading-relaxed">
              NEWZER behält Personen, Unternehmen, Organisationen und Themen für Sie im Blick,
              erkennt relevante Entwicklungen und meldet sich, wenn Ihre Aufmerksamkeit gefragt ist.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/registrieren" className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-fc-blue text-white font-semibold px-6 py-3.5 text-base hover:bg-fc-blue/90 transition-colors shadow-sm" data-testid="hero-cta-primary">
                30 Tage kostenlos testen <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/demo" className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-fc-border bg-white text-fc-ink font-semibold px-6 py-3.5 text-base hover:border-fc-ink/40 transition-colors" data-testid="hero-cta-secondary">
                Live-Demo ansehen
              </Link>
            </div>
            <p className="mt-6 text-sm text-fc-ink/60" data-testid="hero-trust">
              In Deutschland entwickelt · DSGVO-orientiert · Volle Kontrolle
            </p>
          </div>

          <div className="relative flex justify-center lg:justify-end fc-fade-up" style={{ animationDelay: "0.12s" }}>
            <SignalCard />
          </div>
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { Icon: Rss, title: "Beobachten", desc: "NEWZER beobachtet öffentliche Quellen rund um die Uhr – ohne Ihr Zutun.", color: "blue" },
  { Icon: Target, title: "Erkennen", desc: "Relevante Erwähnungen werden aus dem Grundrauschen herausgefiltert.", color: "blue" },
  { Icon: Brain, title: "Einordnen", desc: "NEWZER ordnet Erwähnungen nach Relevanz, Kontext und Dynamik ein.", color: "blue" },
  { Icon: Bell, title: "Warnen", desc: "Bei relevanten Entwicklungen werden Sie benachrichtigt – sofort oder gebündelt, im gewählten Rhythmus.", color: "coral" },
  { Icon: Shield, title: "Handeln", desc: "Sie reagieren mit Vorsprung – bevor es alle wissen.", color: "mint" },
];
const STEP_COLOR = {
  blue: ["bg-fc-blue/8", "text-fc-blue", "border-fc-blue/15"],
  coral: ["bg-fc-coral/10", "text-fc-coral", "border-fc-coral/20"],
  mint: ["bg-fc-mint/12", "text-fc-mint", "border-fc-mint/25"],
};

function StepCard({ i, Icon, title, desc, color }) {
  const [bg, tx, bd] = STEP_COLOR[color];
  return (
    <div className="flex-1 min-w-0 rounded-2xl border border-fc-border bg-white p-5 transition-all hover:border-fc-ink/20 hover:-translate-y-0.5" data-testid={`process-step-${i}`}>
      <div className="flex items-center justify-between">
        <span className={`w-12 h-12 rounded-xl border ${bg} ${bd} flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${tx}`} strokeWidth={1.6} />
        </span>
        <span className="font-head font-bold text-2xl text-fc-ink/10">{String(i + 1).padStart(2, "0")}</span>
      </div>
      <div className="mt-4 font-head font-bold text-fc-ink">{i + 1}. {title}</div>
      <p className="mt-1.5 text-sm text-fc-ink/65 leading-relaxed">{desc}</p>
    </div>
  );
}
function StepConnector() {
  return (
    <div className="flex items-center justify-center w-9 flex-shrink-0 self-center" aria-hidden="true">
      <svg width="36" height="16" viewBox="0 0 36 16" className="text-fc-ink/30">
        <line x1="0" y1="8" x2="26" y2="8" stroke="currentColor" strokeWidth="1.5" className="fc-flow" />
        <path d="M26 3 L34 8 L26 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function Process() {
  return (
    <section id="prozess" className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 lg:py-20 scroll-mt-20">
      <Reveal className="max-w-2xl">
        <p className="text-xs font-semibold tracking-[0.18em] uppercase text-fc-blue">Vom Beobachten zum Handeln</p>
        <h2 className="mt-3 section-headline text-fc-ink">So liefert NEWZER Ihren Vorsprung.</h2>
        <p className="mt-5 text-base lg:text-lg text-fc-ink/70 leading-relaxed">
          Ständiges Nachschauen kostet Zeit und Nerven. NEWZER übernimmt das Beobachten – und meldet sich erst, wenn es wirklich wichtig wird.
        </p>
      </Reveal>
      {/* Desktop: verbundene Prozesskette */}
      <Reveal className="mt-10 hidden lg:flex items-stretch">
        {STEPS.map((s, i) => (
          <Fragment key={s.title}>
            <StepCard i={i} {...s} />
            {i < STEPS.length - 1 && <StepConnector />}
          </Fragment>
        ))}
      </Reveal>
      {/* Mobil / Tablet: gestapelt */}
      <Reveal className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:hidden">
        {STEPS.map((s, i) => <StepCard key={s.title} i={i} {...s} />)}
      </Reveal>
    </section>
  );
}

const LB_KPI = [
  ["32", "Erwähnungen (24 h)", "text-fc-blue"],
  ["8", "Neue Entwicklungen", "text-fc-coral"],
  ["3", "Wichtige Signale", "text-status-critical"],
];
const LB_NAV = [
  [LayoutDashboard, "Übersicht", false],
  [ListFilter, "Lagebild", true],
  [Bell, "Signale", false],
  [Radio, "Beobachtungen", false],
  [FileText, "Briefings", false],
  [LineChart, "Verläufe", false],
  [Settings, "Einstellungen", false],
];
const LB_ROWS = [
  ["Doppelhaushalt der Stadt", "32", "up", "Wichtiges Signal", "text-status-critical bg-status-critical/10"],
  ["Kita-Beitragsdebatte", "18", "up", "Neue Entwicklung", "text-fc-coral bg-fc-coral/10"],
  ["Verkehrskonzept Innenstadt", "12", "flat", "Erwähnung", "text-fc-ink/55 bg-fc-subtle"],
  ["Personalnot in Kliniken", "9", "flat", "Erwähnung", "text-fc-ink/55 bg-fc-subtle"],
];
function Spark({ variant }) {
  const pts = variant === "up" ? "0,14 14,12 28,13 42,7 56,9 70,3 84,5" : "0,10 14,11 28,9 42,11 56,10 70,10 84,9";
  const color = variant === "up" ? "#F47765" : "#1E40AF";
  return (
    <svg viewBox="0 0 84 18" className="w-20 h-4" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
    </svg>
  );
}

/* Signatur: "Nicht nur Treffer. Eine Lage." – Lagebild als DEMO-Produktbeweis. */
function Signature() {
  return (
    <section id="lagebild" className="bg-fc-subtle/40 border-y border-fc-border scroll-mt-20">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-[0.82fr_1.6fr] gap-12 lg:gap-14 items-center">
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-fc-blue">Aus Rauschen wird Klarheit</p>
          <h2 className="mt-3 section-headline text-fc-ink">Nicht nur Treffer.<br />Eine Lage.</h2>
          <p className="mt-5 text-base lg:text-lg text-fc-ink/70 leading-relaxed">
            Eine Suche liefert Treffer. NEWZER verdichtet viele Erwähnungen zu einem verständlichen Lagebild –
            mit Trend, Dynamik und dem einen Signal, das wirklich zählt.
          </p>
          <ul className="mt-7 space-y-3">
            {["Erwähnungen werden zu Themen gebündelt", "Trend und Dynamik werden sichtbar", "Nur relevante Entwicklungen führen zu einer Benachrichtigung"].map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-sm text-fc-ink/75">
                <span className="w-5 h-5 rounded-full bg-fc-mint/15 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-fc-mint" /></span>{t}
              </li>
            ))}
          </ul>
          <Link to="/demo" className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-fc-blue hover:gap-2.5 transition-all" data-testid="lagebild-demo-link">
            Lagebild ansehen <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>

        <Reveal className="relative rounded-2xl border border-fc-border bg-white shadow-[0_40px_90px_-50px_rgba(19,36,58,0.5)] overflow-hidden" data-testid="lagebild-card" style={{ animationDelay: "0.1s" }}>
          <span className="absolute top-3 right-4 z-10 text-[10px] font-bold uppercase tracking-[0.18em] text-fc-ink/40 bg-fc-subtle rounded px-1.5 py-0.5">Demo</span>
          <div className="flex">
            <aside className="hidden md:flex flex-col w-44 flex-shrink-0 bg-fc-ink text-white p-4 gap-1">
              <div className="px-1 pb-2"><Logo size={24} textSize="text-base" variant="white" /></div>
              {LB_NAV.map(([Icon, label, active]) => (
                <span key={label} className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm ${active ? "bg-white/10 text-white font-semibold" : "text-white/55"}`}>
                  <Icon className="w-4 h-4" strokeWidth={1.7} /> {label}
                  {label === "Signale" && <span className="ml-auto text-[10px] font-bold bg-status-critical text-white rounded-full w-4 h-4 flex items-center justify-center">3</span>}
                </span>
              ))}
            </aside>

            <div className="flex-1 min-w-0 p-5">
              <div className="flex items-center gap-2">
                <ListFilter className="w-4 h-4 text-fc-blue" />
                <span className="font-head font-bold text-fc-ink">Lagebild – Aktuelle Themenlage</span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {LB_KPI.map(([v, l, c]) => (
                  <div key={l} className="rounded-xl border border-fc-border bg-fc-subtle/40 px-3 py-3">
                    <div className={`font-head font-extrabold text-2xl leading-none ${c}`}>{v}</div>
                    <div className="mt-1.5 text-[11px] text-fc-ink/55 leading-tight">{l}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-xl border border-fc-border overflow-hidden">
                <div className="hidden sm:grid grid-cols-[1.6fr_0.7fr_0.9fr_1fr] gap-2 px-3 py-2 bg-fc-subtle/50 text-[10px] font-semibold uppercase tracking-wider text-fc-ink/50">
                  <span>Thema</span><span className="text-right">Erwähnungen</span><span>Trend</span><span>Signal</span>
                </div>
                {LB_ROWS.map(([thema, count, trend, sig, sigcls], i) => (
                  <div key={thema} className={`grid grid-cols-[1.4fr_auto] sm:grid-cols-[1.6fr_0.7fr_0.9fr_1fr] gap-2 items-center px-3 py-2.5 ${i < LB_ROWS.length - 1 ? "border-b border-fc-border/60" : ""}`}>
                    <span className="text-sm text-fc-ink font-medium truncate">{thema}</span>
                    <span className="text-sm text-fc-ink/70 sm:text-right tabular-nums text-right">{count}</span>
                    <span className="hidden sm:block"><Spark variant={trend} /></span>
                    <span className="col-span-2 sm:col-span-1">
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 ${sigcls}`}>{sig}</span>
                    </span>
                  </div>
                ))}
              </div>
              <SignalSequence className="mt-4 max-w-md rounded-lg border border-fc-border bg-fc-subtle/40 px-3 py-2.5" />
              <p className="mt-3 text-[11px] text-fc-ink/45">Demo-Darstellung · keine aktuellen Live-Daten.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const AUDIENCES = [
  [Building2, "Kommunen & Behörden", "Öffentliche Stimmung, kritische Themen und Entwicklungen in der Region früh erkennen.", true],
  [Briefcase, "Unternehmen", "Reputationsthemen, Wettbewerber-Bewegungen und Chancen erkennen, bevor sie groß werden."],
  [Users, "Agenturen & Berater", "Mehrere Mandanten gleichzeitig im Blick – strukturiert, nachvollziehbar, belegbar."],
  [User, "Personen & Teams", "Behalten Sie im Blick, was über Sie oder relevante Personen berichtet wird."],
  [Newspaper, "Redaktionen & Medien", "Themen von der ersten Meldung bis zur ganzen Entwicklung kontinuierlich verfolgen."],
];

function Audiences() {
  return (
    <section id="fuer-wen" className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 lg:py-20 scroll-mt-20">
      <Reveal className="max-w-2xl">
        <p className="text-xs font-semibold tracking-[0.18em] uppercase text-fc-blue">Für wen</p>
        <h2 className="mt-3 section-headline text-fc-ink">Für alle, die früh wissen müssen.</h2>
      </Reveal>
      <Reveal className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {AUDIENCES.map(([Icon, t, d, wide], i) => (
          <div key={t} data-testid={`audience-${i}`}
            className={`group rounded-2xl border p-7 transition-all hover:-translate-y-0.5 ${wide ? "lg:col-span-2 bg-fc-ink text-white border-fc-ink" : "bg-white border-fc-border hover:border-fc-ink/25"}`}>
            <span className={`w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105 ${wide ? "bg-white/10 border border-white/10" : "bg-fc-blue/8 border border-fc-blue/12"}`}>
              <Icon className={`w-6 h-6 ${wide ? "text-fc-mint" : "text-fc-blue"}`} strokeWidth={1.6} />
            </span>
            <div className={`mt-5 font-head font-bold text-lg ${wide ? "text-white" : "text-fc-ink"}`}>{t}</div>
            <p className={`mt-2 text-sm leading-relaxed ${wide ? "text-white/70 max-w-md" : "text-fc-ink/65"}`}>{d}</p>
          </div>
        ))}
      </Reveal>
    </section>
  );
}

const SOURCES = [
  [Newspaper, "Nachrichten", "Redaktionelle Berichterstattung öffentlicher Medien."],
  [Globe, "Webseiten", "Öffentlich zugängliche Seiten von Organisationen und Institutionen."],
  [Rss, "RSS-Feeds", "Feeds von Behörden, Medien und Unternehmen werden laufend ausgewertet.", true],
  [Network, "Öffentliche Social-Media", "Öffentlich sichtbare Beiträge – kein verdecktes Monitoring."],
  [Megaphone, "Presseinformationen", "Offizielle Mitteilungen und Ankündigungen."],
];

function Sources() {
  return (
    <section id="quellen" className="bg-fc-subtle/40 border-y border-fc-border scroll-mt-20">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <Reveal className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-fc-blue">Datenbasis</p>
          <h2 className="mt-3 section-headline text-fc-ink">Was NEWZER beobachtet.</h2>
          <p className="mt-5 text-base lg:text-lg text-fc-ink/70 leading-relaxed">
            Ausschließlich öffentlich zugängliche Quellen – transparent, nachvollziehbar und bis zum Ursprung zurückverfolgbar.
          </p>
        </Reveal>
        <Reveal className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SOURCES.map(([Icon, t, d, highlight], i) => (
            <div key={t} data-testid={`source-${i}`}
              className={`group rounded-2xl border p-6 transition-all hover:-translate-y-0.5 ${highlight ? "border-fc-blue bg-white shadow-[0_20px_50px_-30px_rgba(30,64,175,0.4)]" : "border-fc-border bg-white hover:border-fc-ink/25"}`}>
              <div className="flex items-center gap-3">
                <span className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border ${highlight ? "bg-fc-blue/10 border-fc-blue/15" : "bg-fc-ink/[0.04] border-fc-border"}`}>
                  <Icon className={`w-6 h-6 ${highlight ? "text-fc-blue" : "text-fc-ink/70"}`} strokeWidth={1.6} />
                </span>
                <span className="font-head font-bold text-fc-ink text-lg">{t}</span>
                {highlight && <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-fc-blue bg-fc-blue/10 rounded px-2 py-0.5">Laufend ausgewertet</span>}
              </div>
              <p className="mt-3 text-sm text-fc-ink/65 leading-relaxed">{d}</p>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

const TRUST = [
  [MapPin, "In Deutschland entwickelt", "Gebaut und betrieben mit europäischem Datenschutzanspruch."],
  [Link2, "Nachvollziehbare Quellen", "Kein verdecktes Monitoring – jede Meldung ist bis zur Quelle zurückverfolgbar."],
  [Lock, "DSGVO-orientiert", "Datenschutz und Datenminimierung als Grundhaltung."],
  [SlidersHorizontal, "Volle Kontrolle", "Sie bestimmen, was beobachtet wird, wie und wann Sie informiert werden."],
];

function Trust() {
  return (
    <section id="vertrauen" className="bg-fc-ink text-white scroll-mt-20">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <Reveal className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-fc-mint">Vertrauen durch Transparenz</p>
          <h2 className="mt-3 section-headline">Transparenz statt Blackbox.</h2>
          <p className="mt-5 text-base lg:text-lg text-white/70 leading-relaxed">
            NEWZER ist ein Angebot von Dimedial – mit dem Anspruch, Informationen verantwortungsvoll,
            transparent und datenschutzbewusst zugänglich zu machen.
          </p>
        </Reveal>
        <Reveal className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TRUST.map(([Icon, t, d], i) => (
            <div key={t} className="group rounded-2xl bg-white/[0.04] border border-white/10 p-6 transition-all hover:-translate-y-0.5 hover:bg-white/[0.07]" data-testid={`trust-${i}`}>
              <span className="w-12 h-12 rounded-xl bg-white/8 border border-white/10 flex items-center justify-center transition-transform group-hover:scale-105"><Icon className="w-6 h-6 text-fc-mint" strokeWidth={1.6} /></span>
              <div className="mt-5 font-head font-bold">{t}</div>
              <p className="mt-2 text-sm text-white/65 leading-relaxed">{d}</p>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

const ORDER = ["starter", "professional", "business", "agency"];

function Pricing() {
  const [plans, setPlans] = useState(null);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data?.plans || {})).catch(() => {}); }, []);

  return (
    <section id="tarife" className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 lg:py-20 scroll-mt-20">
      <Reveal className="grid lg:grid-cols-[1fr_auto] gap-6 lg:items-end">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-fc-blue">Tarife</p>
          <h2 className="mt-3 section-headline text-fc-ink">Passend für eine einzelne Suche.<br />Stark genug für eine ganze Lage.</h2>
          <p className="mt-5 text-base lg:text-lg text-fc-ink/70 leading-relaxed">
            Transparente Preise inkl. gesetzlicher MwSt. Jahresabrechnung entspricht 10 Monatspreisen – zwei Monate geschenkt.
          </p>
        </div>
        <Link to="/preise" className="inline-flex w-full sm:w-auto sm:justify-self-start items-center justify-center gap-2 px-7 h-[3.25rem] rounded-xl font-semibold bg-fc-blue text-white hover:bg-fc-blue/90 transition-colors shadow-sm whitespace-nowrap" data-testid="pricing-compare">
          Alle Tarife vergleichen <ArrowRight className="w-4 h-4" />
        </Link>
      </Reveal>

      <Reveal className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {ORDER.map((key) => {
          const p = plans?.[key];
          const recommended = p?.recommended;
          const isAgency = key === "agency";
          return (
            <Link key={key} to="/preise" data-testid={`plan-${key}`}
              className={`group rounded-2xl border bg-white p-6 flex flex-col transition-all hover:-translate-y-0.5 ${recommended ? "border-fc-blue ring-1 ring-fc-blue shadow-[0_30px_70px_-45px_rgba(30,64,175,0.5)]" : "border-fc-border hover:border-fc-ink/25"}`}>
              <div className="h-5 mb-3">
                {recommended && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-fc-blue bg-fc-blue/10 rounded px-2 py-0.5"><Star className="w-3 h-3 fill-fc-blue" /> Empfohlen</span>}
              </div>
              <div className="font-head font-bold text-fc-ink text-xl">{p?.name || "—"}</div>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-head font-extrabold text-fc-ink text-3xl">{p ? `${isAgency ? "ab " : ""}${eur(p.price_monthly)} €` : "—"}</span>
                <span className="text-sm text-fc-ink/55 mb-1">/ Monat</span>
              </div>
              <div className="mt-1 text-xs text-fc-ink/55">inkl. MwSt.{p && !isAgency && <> · {eur(p.price_yearly)} € / Jahr</>}</div>
              <p className="mt-4 text-sm text-fc-ink/65 leading-relaxed flex-1">{p?.tagline || ""}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-fc-blue group-hover:gap-2.5 transition-all">Details <ArrowUpRight className="w-4 h-4" /></span>
            </Link>
          );
        })}
      </Reveal>
      <p className="mt-8 text-sm text-fc-ink/65 inline-flex items-center gap-2" data-testid="pricing-trial-note">
        <ShieldCheck className="w-4 h-4 text-fc-mint" /> 30 Tage Professional kostenlos testen · ohne Zahlungsdaten · endet automatisch · keine automatische Verlängerung
      </p>
    </section>
  );
}

function Differentiation() {
  const classic = ["Sie müssen aktiv suchen", "Sie müssen wiederholt nachschauen", "Veränderungen erkennen Sie selbst"];
  const newzer = ["Beobachtet kontinuierlich im Hintergrund", "Erkennt Veränderungen statt nur Treffer", "Verdichtet Entwicklungen und informiert Sie"];
  return (
    <section id="unterschied" className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 lg:py-20 scroll-mt-20" data-testid="differentiation">
      <Reveal className="max-w-2xl">
        <p className="text-xs font-semibold tracking-[0.18em] uppercase text-fc-blue">Der Unterschied</p>
        <h2 className="mt-3 section-headline text-fc-ink">Sie suchen nicht. NEWZER beobachtet.</h2>
        <p className="mt-5 text-base lg:text-lg text-fc-ink/70 leading-relaxed">
          Sie müssen nicht wissen, wann Sie suchen müssen – NEWZER behält es für Sie im Blick.
        </p>
      </Reveal>
      <Reveal className="mt-10 grid md:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-fc-border bg-white/70 p-7" data-testid="diff-classic">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-fc-ink/40">
            <ListFilter className="w-4 h-4" /> Klassische Suche
          </div>
          <ul className="mt-5 space-y-3.5">
            {classic.map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-fc-ink/70"><Minus className="w-5 h-5 text-fc-ink/25 flex-shrink-0 mt-0.5" /> {t}</li>
            ))}
          </ul>
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-fc-blue/20 bg-white p-7 shadow-[0_30px_60px_-40px_rgba(30,64,175,0.4)]" data-testid="diff-newzer">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-fc-blue" />
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-fc-blue">
            <Radio className="w-4 h-4" /> NEWZER
          </div>
          <ul className="mt-5 space-y-3.5">
            {newzer.map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-fc-ink font-medium"><Check className="w-5 h-5 text-fc-mint flex-shrink-0 mt-0.5" /> {t}</li>
            ))}
          </ul>
        </div>
      </Reveal>
    </section>
  );
}

/* Dezenter Google-Alerts-Vergleichs-Teaser – verlinkt auf die ausführliche Vergleichsseite. */
function GoogleAlertsTeaser() {
  const alert = ["Eine Trefferliste – ungeordnet", "Jede Erwähnung gleich gewichtet", "Sie sortieren und bewerten selbst"];
  const nz = ["Erwähnungen nach Relevanz eingeordnet", "Zusammengehörige Meldungen gebündelt", "Alarm im Turnus Ihrer Wahl – bis zum Lagebild"];
  return (
    <section id="google-alerts" className="bg-fc-subtle/40 border-y border-fc-border scroll-mt-20" data-testid="ga-teaser">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <Reveal className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-fc-blue">Umstieg von Google Alerts</p>
          <h2 className="mt-3 section-headline text-fc-ink">Wenn Google Alerts nicht mehr reicht.</h2>
          <p className="mt-5 text-base lg:text-lg text-fc-ink/70 leading-relaxed">
            Ein Alert meldet neue Treffer – NEWZER ordnet sie ein. Statt einer unsortierten Liste erhalten Sie
            relevante Entwicklungen gebündelt und im Rhythmus Ihrer Wahl.
          </p>
        </Reveal>
        <Reveal className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-fc-border bg-white/70 p-7" data-testid="ga-teaser-alert">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-fc-ink/40">
              <Bell className="w-4 h-4" /> Klassischer Alert
            </div>
            <ul className="mt-5 space-y-3.5">
              {alert.map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-fc-ink/70"><Minus className="w-5 h-5 text-fc-ink/25 flex-shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-fc-blue/20 bg-white p-7 shadow-[0_30px_60px_-40px_rgba(30,64,175,0.4)]" data-testid="ga-teaser-newzer">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-fc-blue" />
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-fc-blue">
              <Radio className="w-4 h-4" /> NEWZER
            </div>
            <ul className="mt-5 space-y-3.5">
              {nz.map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-fc-ink font-medium"><Check className="w-5 h-5 text-fc-mint flex-shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal className="mt-8">
          <Link to="/google-alerts-alternative" className="inline-flex items-center gap-1.5 text-sm font-semibold text-fc-blue hover:gap-2.5 transition-all" data-testid="ga-teaser-link">
            NEWZER im Vergleich zu Google Alerts <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function ClosingCTA() {
  return (
    <section className="bg-fc-ink text-white border-t border-white/10">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="max-w-3xl">
          <h2 className="section-headline">Was möchten Sie ab heute früher erkennen?</h2>
          <p className="mt-5 text-base lg:text-lg text-white/70 leading-relaxed max-w-xl">
            Geben Sie Ihr Thema an – NEWZER übernimmt das Beobachten und meldet sich, sobald es wichtig wird.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/registrieren" className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-fc-blue text-white font-semibold px-6 py-3.5 text-base hover:bg-fc-blue/90 transition-colors" data-testid="closing-cta-primary">
              30 Tage kostenlos testen <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/demo" className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/20 bg-white/10 text-white font-semibold px-6 py-3.5 text-base hover:bg-white/20 transition-colors" data-testid="closing-cta-secondary">
              Live-Demo ansehen
            </Link>
          </div>
          <p className="mt-6 text-sm text-white/55 inline-flex items-center gap-2">
            <Check className="w-4 h-4 text-fc-mint" /> Kostenlos starten · endet automatisch · keine automatische Verlängerung
          </p>
        </div>
      </div>
    </section>
  );
}

function pulseAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const m = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.round(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  return `vor ${Math.round(h / 24)} Tg.`;
}
const PULSE_STATUS = {
  signal: { label: "Wichtiges Signal", cls: "text-status-important bg-status-important/10" },
  entwicklung: { label: "Neue Entwicklung", cls: "text-status-attention bg-status-attention/10" },
  beobachtung: { label: "In Beobachtung", cls: "text-fc-blue bg-fc-blue/10" },
};
const PULSE_CAT_TONE = { Politik: "text-fc-blue", Wirtschaft: "text-fc-ink", Region: "text-fc-blue" };

// NEWZER LIVE – kompakter, anonymer öffentlicher Live-Puls (Public-Pool + anonyme 24h-Summen).
function LivePulse() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let active = true;
    const load = () => api.get("/public/pulse").then((r) => { if (active) setData(r.data); }).catch(() => {});
    load();
    const id = setInterval(load, 300000);
    return () => { active = false; clearInterval(id); };
  }, []);
  if (!data) return null;
  const genMin = data.generated_at ? Math.round((Date.now() - new Date(data.generated_at).getTime()) / 60000) : 999;
  const isLive = genMin <= 30;
  const s = data.stats_24h || {};
  const fmt = (n) => (n ?? 0).toLocaleString("de-DE");
  const cards = data.cards || [];
  return (
    <section className="border-b border-fc-border bg-fc-ivory" data-testid="live-pulse">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-11 lg:py-16">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.14em] uppercase text-fc-ink" data-testid="live-pulse-indicator">
            <span className="relative flex h-2 w-2">
              {isLive && <span className="absolute inline-flex h-full w-full rounded-full bg-fc-mint opacity-60 status-pulse text-fc-mint" />}
              <span className={`relative inline-flex rounded-full h-2 w-2 ${isLive ? "bg-fc-mint" : "bg-fc-ink/30"}`} />
            </span>
            {isLive ? "NEWZER LIVE" : "NEWZER STATUS"}
          </span>
          <span className="text-sm text-fc-ink/60">Was NEWZER gerade im Blick hat.</span>
          <span className="text-xs text-fc-ink/40 sm:ml-auto" data-testid="live-pulse-updated">Zuletzt aktualisiert {pulseAgo(data.generated_at)}</span>
        </div>

        <div className="mt-6 grid lg:grid-cols-[1.7fr_1fr] gap-5 items-stretch">
          <div className="flex gap-4 overflow-x-auto pb-1 -mx-1 px-1 snap-x md:grid md:grid-cols-3 md:overflow-visible" data-testid="live-pulse-cards">
            {cards.map((c) => {
              const st = PULSE_STATUS[c.status] || PULSE_STATUS.beobachtung;
              const isText = !c.calm && !c.image;
              const BgIcon = { Politik: Building2, Wirtschaft: TrendingUp, Region: MapPin }[c.category] || Radio;
              return (
                <div key={c.category} className={`relative snap-start shrink-0 w-[82%] sm:w-[60%] md:w-auto rounded-2xl border p-5 flex flex-col overflow-hidden shadow-[0_2px_10px_-3px_rgba(19,36,58,0.07)] ${isText ? "bg-fc-ink border-fc-ink/80" : "bg-white border-fc-border"}`} data-testid={`live-pulse-card-${c.category}`}>
                  {isText && (
                    <>
                      <BgIcon className="absolute -right-5 -bottom-5 w-36 h-36 text-white/[0.06]" strokeWidth={1.25} aria-hidden data-testid={`live-pulse-textbg-${c.category}`} />
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-fc-mint/40 to-transparent" aria-hidden />
                    </>
                  )}
                  {!c.calm && c.image && (
                    <div className="-mx-5 -mt-5 mb-3 overflow-hidden rounded-t-2xl aspect-video bg-fc-subtle" data-testid={`live-pulse-image-${c.category}`}>
                      <img src={c.image} alt="" loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                    </div>
                  )}
                  <div className="relative flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider">
                    <span className={isText ? "text-fc-mint" : (PULSE_CAT_TONE[c.category] || "text-fc-blue")}>{c.category}</span>
                    {!c.calm && c.detected_at && <span className={`font-semibold ${isText ? "text-white/45" : "text-fc-ink/40"}`}>· {pulseAgo(c.detected_at)}</span>}
                  </div>
                  {c.calm ? (
                    <p className="mt-3 text-sm text-fc-ink/55 leading-relaxed flex-1">Ruhige Lage – aktuell keine auffällige neue Entwicklung in diesem Bereich.</p>
                  ) : (
                    <>
                      <p className={`relative mt-3 font-head font-bold flex-1 ${isText ? "text-white text-xl leading-tight line-clamp-4" : "text-fc-ink text-[15px] leading-snug line-clamp-3"}`}>{c.title}</p>
                      <div className="relative mt-4 flex items-center justify-between gap-2">
                        <span className={`text-xs ${isText ? "text-white/55" : "text-fc-ink/55"}`}>{c.sources} {c.sources === 1 ? "Quelle" : "Quellen"} erkannt</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isText ? "text-fc-mint bg-fc-mint/15" : st.cls}`}>{st.label}</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <div className="bg-fc-ink text-white rounded-2xl p-6 flex flex-col justify-center" data-testid="live-pulse-stats">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-white/55">Von NEWZER in den letzten 24 Stunden verarbeitet</div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div><div className="font-head font-extrabold text-2xl leading-none">{fmt(s.processed)}</div><div className="text-[11px] text-white/60 mt-1.5 leading-tight">öffentliche Treffer verarbeitet</div></div>
              <div><div className="font-head font-extrabold text-2xl leading-none text-fc-mint">{fmt(s.relevant)}</div><div className="text-[11px] text-white/60 mt-1.5 leading-tight">relevante Erwähnungen erkannt</div></div>
              <div><div className={`font-head font-extrabold text-2xl leading-none ${(s.signals || 0) > 0 ? "text-fc-coral" : ""}`}>{fmt(s.signals)}</div><div className="text-[11px] text-white/60 mt-1.5 leading-tight">wichtige Signale erkannt</div></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  useEffect(() => {
    // SEO-Title: keyword-orientiert (Medienbeobachtung / Monitoring / Frühwarnsystem) für Google.
    const title = "Medienbeobachtung & Monitoring – Frühwarnsystem | NEWZER";
    // Social-Title: markenstärker für Teaser-Vorschauen (OG/Twitter).
    const ogTitle = "NEWZER – Die Frühwarnzentrale für öffentliche Informationen";
    const desc = "Medienbeobachtung & Frühwarnung: NEWZER beobachtet News, Web und Social Media, erkennt relevante Entwicklungen früh und alarmiert per E-Mail, SMS oder Webhook. DSGVO-orientiert, in Deutschland entwickelt – 30 Tage kostenlos testen.";
    document.title = title;
    upsertMeta("name", "description", desc);
    upsertMeta("name", "robots", "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1");
    upsertMeta("name", "keywords", "Medienbeobachtung, Media Monitoring, Social Media Monitoring, Medienmonitoring, News Monitoring, Frühwarnsystem, Reputationsmanagement, Markenmonitoring, Krisenmonitoring, Erwähnungen überwachen, Google Alerts Alternative");
    upsertLink("canonical", `${SITE_URL}/`);
    upsertMeta("property", "og:title", ogTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", `${SITE_URL}/`);
    upsertMeta("property", "og:image", OG_IMAGE);
    upsertMeta("property", "og:image:alt", "NEWZER – Medienbeobachtung & Frühwarnsystem");
    upsertMeta("property", "og:locale", "de_DE");
    upsertMeta("property", "og:site_name", "NEWZER");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", ogTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", OG_IMAGE);
    upsertMeta("name", "twitter:image:alt", "NEWZER – Medienbeobachtung & Frühwarnsystem");
    if (window.location.hash) {
      const t = document.querySelector(window.location.hash);
      if (t) setTimeout(() => t.scrollIntoView({ behavior: "auto" }), 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-fc-ivory font-body overflow-x-hidden">
      <SiteHeader />
      <main>
        <Hero />
        <LivePulse />
        <ProductStory />
        <Process />
        <Signature />
        <Differentiation />
        <GoogleAlertsTeaser />
        <Audiences />
        <Sources />
        <Trust />
        <Pricing />
        <ClosingCTA />
      </main>
      <SiteFooter />
    </div>
  );
}
